import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getCallerContext } from '../api/endpoints';
import { ApiError } from '../api/http';
import { queryKeys } from '../api/queryKeys';
import type { CallerContext, CallerTenant } from '../api/types';
import { useSignedInUser } from './useSignedInUser';

/**
 * Who the API decided the caller is. `TenantGate` issues this before anything else renders, so every
 * page below it reads a warm cache rather than firing its own request.
 */
export function useCallerContextQuery(): UseQueryResult<CallerContext, Error> {
  return useQuery({
    queryKey: queryKeys.caller.context(),
    queryFn: ({ signal }) => getCallerContext(signal),
    // A refusal is a decision about this caller, not a hiccup; asking again returns the same answer.
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 403) && failureCount < 2,
    staleTime: 5 * 60_000,
  });
}

/** The tenants the caller may file new work under, ordered by name. Empty until one is assigned. */
export function useCallerTenants(): CallerTenant[] {
  return useCallerContextQuery().data?.tenants ?? [];
}

/**
 * Whether the caller administers the service. The API's answer wins so the console and the server
 * cannot disagree; the ID token's role claim stands in only while that answer is missing — which is
 * exactly the not-registered case, where the console still has to decide whether to offer /admin.
 */
export function useIsAdministrator(): boolean {
  const { data } = useCallerContextQuery();
  const { hasAdministratorRole } = useSignedInUser();

  return data?.isAdministrator ?? hasAdministratorRole;
}

/**
 * Whether the caller reaches the full tenant-facing surface, or only the integration contract a
 * bare `registration` role grants. Same precedence as `useIsAdministrator`: the API's answer wins,
 * the ID token's roles stand in while it is missing.
 */
export function useHasFullAccess(): boolean {
  const { data } = useCallerContextQuery();
  const { hasFullAccessRole } = useSignedInUser();

  return data?.hasFullAccess ?? hasFullAccessRole;
}
